/**
 * Trade vocabulary for a steel tube / section plant. These mirror the CHECK
 * constraints on public.clients — keep both sides in sync when adding options.
 */

export type Option<T extends string = string> = { value: T; label: string };

export const CLIENT_TYPES = [
  { value: "oem", label: "OEM" },
  { value: "trader", label: "Trader" },
  { value: "distributor", label: "Distributor / Dealer" },
  { value: "fabricator", label: "Fabricator" },
  { value: "contractor", label: "Project contractor" },
  { value: "government", label: "Government / PSU" },
  { value: "end_user", label: "End user" },
] as const satisfies readonly Option[];

export const GST_TREATMENTS = [
  { value: "registered", label: "Registered — regular" },
  { value: "unregistered", label: "Unregistered" },
  { value: "composition", label: "Composition scheme" },
  { value: "sez", label: "SEZ unit" },
  { value: "export", label: "Export (LUT / with IGST)" },
  { value: "deemed_export", label: "Deemed export" },
] as const satisfies readonly Option[];

export const PAYMENT_TERMS = [
  { value: "advance", label: "100% advance" },
  { value: "against_delivery", label: "Against delivery" },
  { value: "net_15", label: "Net 15 days" },
  { value: "net_30", label: "Net 30 days" },
  { value: "net_45", label: "Net 45 days" },
  { value: "net_60", label: "Net 60 days" },
  { value: "net_90", label: "Net 90 days" },
  { value: "letter_of_credit", label: "Letter of credit" },
] as const satisfies readonly Option[];

export const PRICE_LISTS = [
  { value: "standard", label: "Standard" },
  { value: "oem", label: "OEM contract" },
  { value: "distributor", label: "Distributor" },
  { value: "project", label: "Project" },
  { value: "export", label: "Export" },
] as const satisfies readonly Option[];

export const RATE_BASIS = [
  { value: "per_kg", label: "Per kg" },
  { value: "per_ton", label: "Per tonne" },
  { value: "per_piece", label: "Per piece" },
  { value: "per_meter", label: "Per metre" },
] as const satisfies readonly Option[];

export const DELIVERY_TERMS = [
  { value: "ex_works", label: "Ex-works" },
  { value: "fob", label: "FOB" },
  { value: "cif", label: "CIF" },
  { value: "door_delivery", label: "Door delivery" },
] as const satisfies readonly Option[];

export const FREIGHT_TERMS = [
  { value: "to_pay", label: "To-pay (buyer)" },
  { value: "paid", label: "Paid (seller)" },
  { value: "included", label: "Included in rate" },
] as const satisfies readonly Option[];

export const PACKING_TYPES = [
  { value: "bundled", label: "Bundled" },
  { value: "strapped", label: "Strapped" },
  { value: "loose", label: "Loose" },
  { value: "wooden_pallet", label: "Wooden pallet" },
  { value: "as_per_po", label: "As per PO" },
] as const satisfies readonly Option[];

/** Common finished-goods standards for ERW tubes, hollow sections and pipes. */
export const QUALITY_STANDARDS = [
  "IS 1239 (Part 1)",
  "IS 3589",
  "IS 4923",
  "IS 1161",
  "IS 3601",
  "ASTM A500",
  "ASTM A53",
  "EN 10219",
  "API 5L",
  "Customer drawing",
];

export const INDUSTRY_SEGMENTS = [
  { value: "Automotive", label: "Automotive" },
  { value: "Infrastructure & construction", label: "Infrastructure & construction" },
  { value: "Furniture", label: "Furniture" },
  { value: "Agriculture & irrigation", label: "Agriculture & irrigation" },
  { value: "Scaffolding", label: "Scaffolding" },
  { value: "Solar mounting", label: "Solar mounting" },
  { value: "Oil & gas", label: "Oil & gas" },
  { value: "Water supply", label: "Water supply" },
  { value: "General engineering", label: "General engineering" },
] as const satisfies readonly Option[];

export const ADDRESS_TYPES = [
  { value: "billing", label: "Billing" },
  { value: "shipping", label: "Shipping / plant" },
  { value: "other", label: "Other" },
] as const satisfies readonly Option[];

export const CONTACT_TAGS = [
  { value: "purchase", label: "Purchase" },
  { value: "accounts", label: "Accounts" },
  { value: "engineering", label: "Engineering" },
  { value: "quality", label: "Quality" },
  { value: "stores", label: "Stores" },
  { value: "dispatch", label: "Dispatch" },
  { value: "management", label: "Management" },
  { value: "admin", label: "Admin" },
] as const satisfies readonly Option[];

export const INDIAN_STATES: { name: string; code: string }[] = [
  { name: "Andhra Pradesh", code: "37" },
  { name: "Arunachal Pradesh", code: "12" },
  { name: "Assam", code: "18" },
  { name: "Bihar", code: "10" },
  { name: "Chandigarh", code: "04" },
  { name: "Chhattisgarh", code: "22" },
  { name: "Delhi", code: "07" },
  { name: "Goa", code: "30" },
  { name: "Gujarat", code: "24" },
  { name: "Haryana", code: "06" },
  { name: "Himachal Pradesh", code: "02" },
  { name: "Jammu & Kashmir", code: "01" },
  { name: "Jharkhand", code: "20" },
  { name: "Karnataka", code: "29" },
  { name: "Kerala", code: "32" },
  { name: "Madhya Pradesh", code: "23" },
  { name: "Maharashtra", code: "27" },
  { name: "Manipur", code: "14" },
  { name: "Meghalaya", code: "17" },
  { name: "Mizoram", code: "15" },
  { name: "Nagaland", code: "13" },
  { name: "Odisha", code: "21" },
  { name: "Puducherry", code: "34" },
  { name: "Punjab", code: "03" },
  { name: "Rajasthan", code: "08" },
  { name: "Sikkim", code: "11" },
  { name: "Tamil Nadu", code: "33" },
  { name: "Telangana", code: "36" },
  { name: "Tripura", code: "16" },
  { name: "Uttar Pradesh", code: "09" },
  { name: "Uttarakhand", code: "05" },
  { name: "West Bengal", code: "19" },
];

export function labelFor(options: readonly Option[], value: string | null | undefined) {
  if (!value) return "—";
  return options.find((o) => o.value === value)?.label ?? value;
}

const INR = new Intl.NumberFormat("en-IN", {
  style: "currency",
  currency: "INR",
  maximumFractionDigits: 0,
});

export function formatMoney(value: number | string | null | undefined) {
  if (value === null || value === undefined || value === "") return "—";
  const n = typeof value === "number" ? value : Number(value);
  if (Number.isNaN(n)) return "—";
  return INR.format(n);
}
